import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Users,
  Trophy,
  TrendingUp,
  Calendar,
  ChevronRight,
  Loader2,
  Target,
  Award,
  Activity,
  User,
  Clock,
  Star,
  Shield,
  ShieldCheck,
  Lock,
  Trash2,
  Search,
  Filter,
  Download,
  MoreHorizontal,
  UserCog,
  Building2,
  Tag,
  History,
  CheckCircle2,
  XCircle,
  ChevronDown,
  Play
} from "lucide-react";

export default function Team() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  
  // Data state
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  
  // UI state
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [activityFilter, setActivityFilter] = useState("all");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  
  // Member detail state
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberSessions, setMemberSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  
  // Dialog state
  const [editUserDialog, setEditUserDialog] = useState({ open: false, user: null });
  const [bulkUpdateDialog, setBulkUpdateDialog] = useState({ open: false });
  const [activityLogDialog, setActivityLogDialog] = useState({ open: false });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, sessionId: null, sessionName: null });
  const [deleteUserDialog, setDeleteUserDialog] = useState({ open: false, user: null });
  
  // Loading states
  const [updatingUser, setUpdatingUser] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (user?.is_manager || user?.is_admin) {
      fetchTeamData();
    } else {
      setLoading(false);
    }
    // fetchTeamData is a stable helper; re-run only when the user changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchTeamData = async () => {
    setLoading(true);
    try {
      const promises = [
        fetch(`${API}/team/stats`, { credentials: "include" }),
        fetch(`${API}/team/members`, { credentials: "include" }),
        fetch(`${API}/team/departments`, { credentials: "include" })
      ];
      
      if (user?.is_admin) {
        promises.push(fetch(`${API}/team/activity-logs?limit=20`, { credentials: "include" }));
      }

      const responses = await Promise.all(promises);

      if (responses[0].ok) setStats(await responses[0].json());
      if (responses[1].ok) setMembers(await responses[1].json());
      if (responses[2].ok) setDepartments(await responses[2].json());
      if (user?.is_admin && responses[3]?.ok) setActivityLogs(await responses[3].json());
    } catch (error) {
      console.error("Failed to fetch team data:", error);
      toast.error("Failed to load team data");
    } finally {
      setLoading(false);
    }
  };

  // Filtered members
  const filteredMembers = useMemo(() => {
    return members.filter(member => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!member.name?.toLowerCase().includes(query) && 
            !member.email?.toLowerCase().includes(query)) {
          return false;
        }
      }
      
      // Role filter
      if (roleFilter !== "all") {
        if (roleFilter === "admin" && !member.is_admin) return false;
        if (roleFilter === "manager" && !member.is_manager) return false;
        if (roleFilter === "user" && (member.is_admin || member.is_manager)) return false;
      }
      
      // Department filter
      if (departmentFilter !== "all") {
        if (departmentFilter === "none" && member.department) return false;
        if (departmentFilter !== "none" && member.department !== departmentFilter) return false;
      }
      
      // Activity filter
      if (activityFilter !== "all") {
        if (activityFilter === "active" && !member.is_active) return false;
        if (activityFilter === "inactive" && member.is_active) return false;
      }
      
      return true;
    });
  }, [members, searchQuery, roleFilter, departmentFilter, activityFilter]);

  const handleSelectAll = () => {
    if (selectedMembers.length === filteredMembers.length) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(filteredMembers.map(m => m.user_id));
    }
  };

  const handleSelectMember = (userId) => {
    setSelectedMembers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const updateUserRole = async (userId, field, value) => {
    setUpdatingUser(userId);
    try {
      const endpoint = field === "is_admin" ? "/team/set-admin" : "/team/set-manager";
      const response = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ user_id: userId, [field]: value })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchTeamData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update user");
      }
    } catch (error) {
      toast.error("Failed to update user");
    } finally {
      setUpdatingUser(null);
    }
  };

  const updateUserMetadata = async (userId, department, tags) => {
    setUpdatingUser(userId);
    try {
      const response = await fetch(`${API}/team/update-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ user_id: userId, department, tags })
      });
      
      if (response.ok) {
        toast.success("User updated");
        fetchTeamData();
        setEditUserDialog({ open: false, user: null });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update user");
      }
    } catch (error) {
      toast.error("Failed to update user");
    } finally {
      setUpdatingUser(null);
    }
  };

  const handleBulkUpdate = async (updates) => {
    if (selectedMembers.length === 0) return;
    
    try {
      const response = await fetch(`${API}/team/bulk-update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ user_ids: selectedMembers, ...updates })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Updated ${data.updated_count} users`);
        setSelectedMembers([]);
        setBulkUpdateDialog({ open: false });
        fetchTeamData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update users");
      }
    } catch (error) {
      toast.error("Failed to update users");
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API}/team/export-csv`, { credentials: "include" });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `team_export_${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Export downloaded");
      } else {
        toast.error("Failed to export");
      }
    } catch (error) {
      toast.error("Failed to export");
    } finally {
      setExporting(false);
    }
  };

  const fetchMemberSessions = async (member) => {
    setSelectedMember(member);
    setMemberSessions([]);
    setLoadingSessions(true);
    try {
      const response = await fetch(`${API}/team/member/${member.user_id}/sessions`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setMemberSessions(data.sessions || []);
      } else {
        toast.error("Failed to load sessions");
      }
    } catch (error) {
      toast.error("Failed to load sessions");
    } finally {
      setLoadingSessions(false);
    }
  };

  const handleDeleteSession = async () => {
    if (!deleteDialog.sessionId) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${API}/sessions/${deleteDialog.sessionId}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        setMemberSessions(prev => prev.filter(s => s.session_id !== deleteDialog.sessionId));
        toast.success("Session deleted");
        fetchTeamData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete");
      }
    } catch (error) {
      toast.error("Failed to delete session");
    } finally {
      setDeleting(false);
      setDeleteDialog({ open: false, sessionId: null, sessionName: null });
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserDialog.user) return;
    setDeletingUser(true);
    try {
      const res = await fetch(`${API}/admin/users/${deleteUserDialog.user.user_id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        toast.success("User deleted", {
          description: `Removed ${data.deleted_counts?.roleplay_sessions || 0} sessions, ${data.deleted_counts?.recordings || 0} recordings`,
        });
        if (selectedMember?.user_id === deleteUserDialog.user.user_id) {
          setSelectedMember(null);
          setMemberSessions([]);
        }
        await fetchTeamData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to delete user");
      }
    } catch {
      toast.error("Failed to delete user");
    } finally {
      setDeletingUser(false);
      setDeleteUserDialog({ open: false, user: null });
    }
  };

  const getRoleBadge = (member) => {
    if (member.is_admin) return <Badge className="bg-amber-500 text-white">Admin</Badge>;
    if (member.is_manager) return <Badge variant="secondary">Manager</Badge>;
    return <Badge variant="outline">User</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric"
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit", minute: "2-digit"
    });
  };

  // Not a manager - show restricted message
  if (!user?.is_manager && !user?.is_admin) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="team-page">
        <Card className="text-center">
          <CardHeader>
            <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-muted-foreground" />
            </div>
            <CardTitle className="text-2xl">Team Management</CardTitle>
            <CardDescription className="text-base">
              Manager access is required to view team performance.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Contact your administrator to request manager access.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="team-page">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold">Team Management</h1>
          <p className="text-muted-foreground">Manage your team and track performance</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {user?.is_admin && (
            <>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setActivityLogDialog({ open: true })}
                data-testid="activity-log-btn"
              >
                <History className="w-4 h-4 mr-2" />
                Activity Log
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleExportCSV}
                disabled={exporting}
                data-testid="export-csv-btn"
              >
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Export CSV
              </Button>
            </>
          )}
          <Badge variant="outline" className="gap-1">
            <Users className="w-3 h-3" />
            {stats?.team_size || 0} Members
          </Badge>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Sessions</p>
                <p className="text-2xl sm:text-3xl font-bold">{stats?.total_sessions || 0}</p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Team Average</p>
                <p className="text-2xl sm:text-3xl font-bold">{stats?.team_average_score || 0}%</p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                <Target className="w-5 h-5 sm:w-6 sm:h-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Best Score</p>
                <p className="text-2xl sm:text-3xl font-bold">{stats?.team_best_score || 0}%</p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-yellow-500/10 flex items-center justify-center">
                <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active (30d)</p>
                <p className="text-2xl sm:text-3xl font-bold">{stats?.active_members || 0}</p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-500/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Team Members Panel */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Team Members
              </CardTitle>
              
              {/* Bulk Actions */}
              {selectedMembers.length > 0 && user?.is_admin && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{selectedMembers.length} selected</span>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setBulkUpdateDialog({ open: true })}
                  >
                    <UserCog className="w-4 h-4 mr-1" />
                    Bulk Edit
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => setSelectedMembers([])}
                  >
                    Clear
                  </Button>
                </div>
              )}
            </div>
            
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mt-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-full sm:w-[130px]">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="admin">Admins</SelectItem>
                  <SelectItem value="manager">Managers</SelectItem>
                  <SelectItem value="user">Users</SelectItem>
                </SelectContent>
              </Select>
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger className="w-full sm:w-[140px]">
                  <SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Depts</SelectItem>
                  <SelectItem value="none">No Dept</SelectItem>
                  {departments.map(dept => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={activityFilter} onValueChange={setActivityFilter}>
                <SelectTrigger className="w-full sm:w-[120px]">
                  <SelectValue placeholder="Activity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          
          <CardContent>
            {/* Select All */}
            {user?.is_admin && filteredMembers.length > 0 && (
              <div className="flex items-center gap-2 pb-3 border-b border-border mb-3">
                <Checkbox
                  checked={selectedMembers.length === filteredMembers.length && filteredMembers.length > 0}
                  onCheckedChange={handleSelectAll}
                />
                <span className="text-sm text-muted-foreground">Select all ({filteredMembers.length})</span>
              </div>
            )}
            
            <ScrollArea className="h-[500px]">
              <div className="space-y-2">
                {filteredMembers.map((member) => (
                  <div
                    key={member.user_id}
                    className={`flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors border ${
                      selectedMembers.includes(member.user_id) ? "border-primary bg-primary/5" : "border-transparent"
                    }`}
                    data-testid={`team-member-${member.user_id}`}
                  >
                    {user?.is_admin && (
                      <Checkbox
                        checked={selectedMembers.includes(member.user_id)}
                        onCheckedChange={() => handleSelectMember(member.user_id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                    
                    <div 
                      className="flex items-center gap-3 flex-1 cursor-pointer"
                      onClick={() => fetchMemberSessions(member)}
                    >
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={member.picture} alt={member.name} />
                        <AvatarFallback>{member.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium truncate">{member.name}</p>
                          {getRoleBadge(member)}
                          {member.is_active ? (
                            <Badge variant="outline" className="text-green-500 border-green-500/30 text-xs">
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Active
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground text-xs">
                              <XCircle className="w-3 h-3 mr-1" /> Inactive
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span className="truncate">{member.email}</span>
                          {member.department && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {member.department}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right hidden sm:block">
                        <p className="font-medium">{member.average_score}%</p>
                        <p className="text-xs text-muted-foreground">{member.total_sessions} sessions</p>
                      </div>
                    </div>
                    
                    {user?.is_admin && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditUserDialog({ open: true, user: member })}>
                            <UserCog className="w-4 h-4 mr-2" />
                            Edit User
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => updateUserRole(member.user_id, "is_manager", !member.is_manager)}
                            disabled={member.is_admin || updatingUser === member.user_id}
                          >
                            <Shield className="w-4 h-4 mr-2" />
                            {member.is_manager ? "Remove Manager" : "Make Manager"}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => updateUserRole(member.user_id, "is_admin", !member.is_admin)}
                            disabled={member.user_id === user.user_id || updatingUser === member.user_id}
                          >
                            <ShieldCheck className="w-4 h-4 mr-2" />
                            {member.is_admin ? "Remove Admin" : "Make Admin"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => setDeleteUserDialog({ open: true, user: member })}
                            disabled={member.user_id === user.user_id}
                            className="text-destructive focus:text-destructive"
                            data-testid={`delete-user-${member.user_id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete User
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    
                    <ChevronRight 
                      className="w-4 h-4 text-muted-foreground cursor-pointer" 
                      onClick={() => fetchMemberSessions(member)}
                    />
                  </div>
                ))}
                
                {filteredMembers.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No members match your filters
                  </p>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Right Panel - Sessions/Top Performers */}
        <div className="space-y-6">
          {/* Member Sessions */}
          {selectedMember ? (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Sessions</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => {setSelectedMember(null); setMemberSessions([]);}}>
                    Clear
                  </Button>
                </div>
                <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <Avatar>
                    <AvatarImage src={selectedMember.picture} alt={selectedMember.name} />
                    <AvatarFallback>{selectedMember.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{selectedMember.name}</p>
                    <p className="text-sm text-muted-foreground">{selectedMember.total_sessions} sessions • {selectedMember.average_score}% avg</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingSessions ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : (
                  <ScrollArea className="h-[350px]">
                    <div className="space-y-2">
                      {memberSessions.map((session) => (
                        <div
                          key={session.session_id}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors border border-border"
                        >
                          <div 
                            className="flex items-center gap-3 flex-1 cursor-pointer"
                            onClick={() => navigate(`/team/member/${selectedMember.user_id}/session/${session.session_id}`)}
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              session.status === "completed" ? "bg-green-500/10" : "bg-yellow-500/10"
                            }`}>
                              {session.status === "completed" ? (
                                <Star className="w-4 h-4 text-green-500" />
                              ) : (
                                <Clock className="w-4 h-4 text-yellow-500" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{session.scenario_name}</p>
                              <p className="text-xs text-muted-foreground">{formatDate(session.started_at)}</p>
                            </div>
                          </div>
                          {session.score != null && (
                            <Badge variant={session.score >= 70 ? "default" : "secondary"}>
                              {session.score}%
                            </Badge>
                          )}
                          {session.has_recording && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-primary"
                              onClick={() => navigate(`/team/member/${selectedMember.user_id}/session/${session.session_id}`)}
                              data-testid={`play-recording-${session.session_id}`}
                            >
                              <Play className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => setDeleteDialog({
                              open: true,
                              sessionId: session.session_id,
                              sessionName: session.scenario_name
                            })}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      {memberSessions.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-8">
                          No sessions found
                        </p>
                      )}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          ) : (
            /* Top Performers */
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-500" />
                  Top Performers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats?.top_performers?.map((performer, index) => (
                    <div key={performer.user_id} className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? "bg-yellow-500 text-yellow-950" :
                        index === 1 ? "bg-gray-300 text-gray-700" :
                        index === 2 ? "bg-amber-600 text-white" :
                        "bg-muted text-muted-foreground"
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{performer.name}</p>
                        <p className="text-xs text-muted-foreground">{performer.session_count} sessions</p>
                      </div>
                      <Badge variant="secondary">{performer.average_score}%</Badge>
                    </div>
                  ))}
                  {(!stats?.top_performers || stats.top_performers.length === 0) && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No completed sessions yet
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialog.open} onOpenChange={(open) => setEditUserDialog({ open, user: open ? editUserDialog.user : null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update department and tags for {editUserDialog.user?.name}
            </DialogDescription>
          </DialogHeader>
          {editUserDialog.user && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Department</Label>
                <Input
                  placeholder="e.g., Sales, Marketing"
                  defaultValue={editUserDialog.user.department || ""}
                  id="edit-department"
                />
              </div>
              <div className="space-y-2">
                <Label>Tags (comma separated)</Label>
                <Input
                  placeholder="e.g., senior, team-lead"
                  defaultValue={(editUserDialog.user.tags || []).join(", ")}
                  id="edit-tags"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserDialog({ open: false, user: null })}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                const dept = document.getElementById("edit-department").value;
                const tags = document.getElementById("edit-tags").value
                  .split(",")
                  .map(t => t.trim())
                  .filter(t => t);
                updateUserMetadata(editUserDialog.user.user_id, dept, tags);
              }}
              disabled={updatingUser}
            >
              {updatingUser && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Update Dialog */}
      <Dialog open={bulkUpdateDialog.open} onOpenChange={(open) => setBulkUpdateDialog({ open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Update {selectedMembers.length} Users</DialogTitle>
            <DialogDescription>
              Apply changes to all selected users
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Set Department</Label>
              <Input placeholder="Leave empty to skip" id="bulk-department" />
            </div>
            <div className="space-y-2">
              <Label>Set Role</Label>
              <Select defaultValue="skip" id="bulk-role">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip">Don't change</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkUpdateDialog({ open: false })}>
              Cancel
            </Button>
            <Button onClick={() => {
              const dept = document.getElementById("bulk-department").value;
              const roleSelect = document.querySelector('[id="bulk-role"]');
              const role = roleSelect?.textContent?.toLowerCase();
              
              const updates = {};
              if (dept) updates.department = dept;
              if (role === "manager") updates.is_manager = true;
              if (role === "user") updates.is_manager = false;
              
              if (Object.keys(updates).length > 0) {
                handleBulkUpdate(updates);
              } else {
                toast.error("No changes selected");
              }
            }}>
              Apply Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity Log Dialog */}
      <Dialog open={activityLogDialog.open} onOpenChange={(open) => setActivityLogDialog({ open })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Activity Log
            </DialogTitle>
            <DialogDescription>
              Recent admin and manager actions
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[400px]">
            <div className="space-y-3">
              {activityLogs.map((log, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Activity className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <span className="font-medium">{log.actor_name}</span>
                      {" "}
                      <span className="text-muted-foreground">{log.action.replace(/_/g, " ")}</span>
                      {log.target_user_name && (
                        <>
                          {" → "}
                          <span className="font-medium">{log.target_user_name}</span>
                        </>
                      )}
                    </p>
                    {log.details && (
                      <p className="text-xs text-muted-foreground mt-1">{log.details}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDate(log.timestamp)} at {formatTime(log.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
              {activityLogs.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No activity logs yet
                </p>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Delete Session Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !deleting && setDeleteDialog(prev => ({ ...prev, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Session?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete "{deleteDialog.sessionName}"? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSession}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete User Dialog */}
      <AlertDialog
        open={deleteUserDialog.open}
        onOpenChange={(open) => !deletingUser && setDeleteUserDialog({ open, user: open ? deleteUserDialog.user : null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user "{deleteUserDialog.user?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the user account and ALL their data — sessions,
              recordings, scenarios, templates, analyses, and embeds. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingUser}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              disabled={deletingUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-user-btn"
            >
              {deletingUser ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete User & Data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
